// ===============================================================
// File: src/contexts/WorkshopContext.jsx
// Description: Workshops + Bookings with cross-device sync
// - Persists via StorageContext.writeDomain (Drive/Folder)
// - Mirrors to localStorage keys (mudo:workshops/...)
// - Listens to mudo:* restore events + storage events
// - Drive-aware polling using loadJSONFresh to bypass LS mirror
// ===============================================================

import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useStorage } from "@/contexts/StorageContext";
import { useNotifications } from "./NotificationsContext";
import { supabase } from "../supabaseClient";
import { useAuth } from "./AuthContext";
import { getStudioWorkshops } from "../lib/mudoNetwork/workshopsApi";

const WorkshopContext = createContext(null);
export const useWorkshops = () => useContext(WorkshopContext);

// canonical files (must match StorageContext.DOMAIN_FILES)
const PATH_WORKSHOPS = "workshops/workshops.json";
const PATH_BOOKINGS  = "workshops/bookings.json";

function lsKey(p) { return `mudo:${p}`; }
function safeArr(v) { return Array.isArray(v) ? v : []; }
function deepEqual(a, b) {
  try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
}
function nowIso() { return new Date().toISOString(); }

export function WorkshopProvider({ children }) {
  const storageContext = useStorage();
  const {
    readDomain, writeDomain, loadJSON, loadJSONFresh,
    isDriveLinked, hasWorkspace,
  } = storageContext || {};

  const { user, studioProfile } = useAuth() || {};

  const drivePollJitterMsRef = useRef(Math.floor(Math.random() * 20000));

  const [workshops, setWorkshops] = useState([]);
  const [bookings, setBookings]   = useState([]);
  const [loading, setLoading]     = useState(true);

  const workshopsRef = useRef(workshops);
  const bookingsRef  = useRef(bookings);
  useEffect(() => { workshopsRef.current = workshops; }, [workshops]);
  useEffect(() => { bookingsRef.current  = bookings;  }, [bookings]);

  const { addNotification } = useNotifications() || {};
  
  const mirrorToLocal = useCallback((path, value) => {
    try { localStorage.setItem(lsKey(path), JSON.stringify(value)); } catch {}
  }, []);

  const hydrate = useCallback(async () => {
    setLoading(true);
    try {
      // If no storage context (visitor mode), DON'T load from localStorage
      // Visitor mode should only see workshops from the network/API, not local data
      if (!loadJSON) {
        console.log("[WorkshopContext] Visitor mode detected - not loading local workshops");
        setWorkshops([]);
        setBookings([]);
      } else {
        // PRIORITY: Load from localStorage FIRST (it has latest changes)
        // Only load from Drive as fallback
        let workshops = safeArr(null);
        const lsWorkshops = JSON.parse(localStorage.getItem(lsKey(PATH_WORKSHOPS)) || '[]');
        
        if (lsWorkshops.length > 0) {
          workshops = lsWorkshops;
          console.log(`[WorkshopContext] Loaded ${workshops.length} workshops from localStorage (priority)`);
        } else {
          // Fallback: load from Drive if localStorage is empty
          const driveWorkshops = await loadJSON(PATH_WORKSHOPS, []);
          workshops = safeArr(driveWorkshops);
          console.log(`[WorkshopContext] Loaded ${workshops.length} workshops from Drive (fallback)`);
        }
        
        const lsBookings = JSON.parse(localStorage.getItem(lsKey(PATH_BOOKINGS)) || '[]');
        const bookings = lsBookings.length > 0 
          ? lsBookings 
          : safeArr(await loadJSON(PATH_BOOKINGS, []));
        
        const studioId = studioProfile?.id;

        // Filter out workshops belonging to other studios (keep those without studio_id to avoid accidental loss)
        const filtered = studioId
          ? workshops.filter(w => !w.studio_id || w.studio_id === studioId)
          : workshops;

        console.log(`[WorkshopContext] Loaded ${filtered.length}/${workshops.length} workshops after studio filter:`, filtered.map(w => ({ id: w.id, title: w.title, studio_id: w.studio_id })));
        workshops = filtered;
        
        // FALLBACK: If vault is empty but user is authenticated, pull from Supabase to prevent data loss
        if (workshops.length === 0 && user && studioProfile?.id) {
          console.log('[WorkshopContext] Vault is empty, attempting to restore from Supabase...');
          try {
            const { data: supabaseWorkshops, error } = await getStudioWorkshops(
              studioProfile.id,
              { includeArchived: false, limit: 1000 }
            );
            
            if (!error && supabaseWorkshops && supabaseWorkshops.length > 0) {
              // Transform Supabase → vault format
              workshops = supabaseWorkshops.map(sw => ({
                id: sw.id,
                title: sw.title || "",
                description: sw.description || "",
                startISO: sw.start_at,
                date: sw.start_at,
                durationMins: sw.duration_mins || 120,
                capacity: sw.capacity || 10,
                price: (sw.price_cents || 0) / 100,
                level: sw.skill_level || "All Levels",
                isPublic: sw.is_published !== false,
                  studio_id: studioProfile.id,
              }));
              console.log(`[WorkshopContext] Restored ${workshops.length} workshops from Supabase`);
            }
          } catch (err) {
            console.warn('[WorkshopContext] Failed to restore from Supabase:', err.message);
          }
        }
        
        // Vault is the source of truth for Studio world
        // We sync TO Supabase when creating/updating/deleting, but don't merge FROM Supabase on hydrate normally
        // This prevents deleted workshops from resurrecting on page refresh
        setWorkshops(workshops);
        setBookings(bookings);
        
        // Always ensure both Drive and localStorage are in sync
        mirrorToLocal(PATH_WORKSHOPS, workshops);
        mirrorToLocal(PATH_BOOKINGS, bookings);
      }
    } finally {
      setLoading(false);
    }
  }, [loadJSON, mirrorToLocal, user, studioProfile]);

  useEffect(() => { hydrate(); }, [hydrate]);

  const persistWorkshops = useCallback(async (next) => {
    const arr = safeArr(next);
    console.log('[WorkshopContext.persistWorkshops] Saving', arr.length, 'workshops to drive:', arr.map(w => ({ id: w.id, title: w.title })));
    
    // Try to write to Drive if available, but don't fail if it's not
    if (writeDomain) {
      try {
        await writeDomain("workshops_list", arr);
        console.log('[WorkshopContext.persistWorkshops] Successfully wrote to drive');
      } catch (err) {
        console.warn('[WorkshopContext.persistWorkshops] Failed to write to drive (continuing with localStorage only):', err?.message);
      }
    } else {
      console.log('[WorkshopContext.persistWorkshops] No writeDomain available, using localStorage only');
    }
    
    // Always mirror to localStorage as fallback
    mirrorToLocal(PATH_WORKSHOPS, arr);
    setWorkshops(arr);
    window.dispatchEvent(new Event("mudo:workshops-updated"));
  }, [writeDomain, mirrorToLocal]);

  const persistBookings = useCallback(async (next) => {
    const arr = safeArr(next);
    if (writeDomain) {
      await writeDomain("workshops_bookings", arr);
    }
    mirrorToLocal(PATH_BOOKINGS, arr);
    setBookings(arr);
    window.dispatchEvent(new Event("mudo:workshops-updated"));
  }, [writeDomain, mirrorToLocal]);

  const upsertWorkshop = useCallback(async (ws) => {
    console.log('[WorkshopContext] upsertWorkshop START (local-only):', { id: ws.id, title: ws.title, isPublic: ws.isPublic });

    // Supabase sync disabled: persist locally only
    if (!ws.id) {
      throw new Error('Cannot save workshop without ID. Generate a stable ID at creation.');
    }

    const id = String(ws.id);
    const next = [...workshopsRef.current];
    const ix = next.findIndex(x => x.id === id);
    const record = {
      ...ws,
      id,
      updatedAt: nowIso(),
      studio_id: studioProfile?.id || ws.studio_id,
    };
    if (ix >= 0) next[ix] = { ...next[ix], ...record };
    else next.push(record);

    await persistWorkshops(next);

    // Emit local update event
    window.dispatchEvent(new CustomEvent('workshops:changed', {
      detail: {
        studioId: studioProfile?.id,
        workshopId: id,
        action: ix >= 0 ? 'updated' : 'created'
      }
    }));

    return record;
  }, [persistWorkshops, studioProfile]);

  const removeWorkshop = useCallback(async (id) => {
    const next = workshopsRef.current.filter(w => w.id !== id);
    
    console.log('[WorkshopContext] removeWorkshop called for:', { id, beforeCount: workshopsRef.current.length, afterCount: next.length });
    
    // Persist to vault/localStorage
    await persistWorkshops(next);

    // Supabase archive disabled: deletion is local-only. Publish/unpublish toggle controls visibility.
  }, [persistWorkshops, user, studioProfile, supabase]);

  const bookWorkshop = useCallback(async (payload) => {
    // ✅ Bookings are ALWAYS new, so generate UUID here
    const id = (crypto.randomUUID ? crypto.randomUUID() : `bk_${Date.now()}_${Math.random().toString(36).slice(2,7)}`);
    const rec = {
      id,
      workshopId: String(payload.workshopId || ""),
      userName: (payload.userName || "").trim(),
      email: (payload.email || "").trim(),
      seats: Math.max(1, Number(payload.seats || 1)),
      piecesPerSeat: Math.max(1, Number(payload.piecesPerSeat || 1)),
      consent: !!payload.consent,
      notes: payload.notes || "",
      createdAt: nowIso(),
      updatedAt: nowIso(),
      status: "booked",
    };
    const next = [...bookingsRef.current, rec];
    await persistBookings(next);
    if (addNotification) {
      const workshop = workshopsRef.current.find((w) => w.id === rec.workshopId);
      const title = workshop?.title || workshop?.name || "Workshop booking";
      const participant = rec.userName || rec.email || "Guest";
      addNotification({
        world: "studio",
        type: "workshop-booking",
        title: `Workshop booking: ${title}`,
        body: `${participant} booked ${rec.seats} seat${rec.seats === 1 ? "" : "s"}.`,
        target: { entity: "workshop", entityId: rec.workshopId, route: "/workshops" },
        severity: "info",
        meta: { bookingId: rec.id, workshopId: rec.workshopId },
      });
      addNotification({
        world: "visitor",
        type: "workshop-booking",
        title: `You're booked for ${title}`,
        body: `Seats reserved: ${rec.seats}. Watch your inbox for prep notes.`,
        target: { entity: "workshop", route: "/discover/workshops" },
        severity: "success",
        meta: { bookingId: rec.id, workshopId: rec.workshopId },
      });
    }
    return rec;
  }, [persistBookings, addNotification]);

  const updateBooking = useCallback(async (id, patch) => {
    const next = bookingsRef.current.map(b =>
      b.id === id ? { ...b, ...patch, updatedAt: nowIso() } : b
    );
    await persistBookings(next);
    if (addNotification && patch) {
      const booking = next.find((b) => b.id === id);
      if (booking) {
        const workshop = workshopsRef.current.find((w) => w.id === booking.workshopId);
        const title = workshop?.title || workshop?.name || "Workshop";
        if (patch.status && patch.status !== "booked") {
          addNotification({
            world: "visitor",
            type: "workshop-message",
            title: `Booking update: ${title}`,
            body: `Status changed to ${patch.status}.`,
            target: { entity: "workshop", route: "/discover/workshops" },
            severity: patch.status === "cancelled" ? "warning" : "info",
            meta: { bookingId: id, workshopId: booking.workshopId },
          });
        }
      }
    }
  }, [persistBookings, addNotification]);

  const cancelBooking = useCallback(async (id) => {
    const existing = bookingsRef.current.find((b) => b.id === id);
    const next = bookingsRef.current.map(b =>
      b.id === id ? { ...b, status: "cancelled", updatedAt: nowIso() } : b
    );
    await persistBookings(next);
    if (addNotification && existing) {
      const workshop = workshopsRef.current.find((w) => w.id === existing.workshopId);
      const title = workshop?.title || workshop?.name || "Workshop";
      addNotification({
        world: "studio",
        type: "workshop-message",
        title: `Booking cancelled: ${title}`,
        body: `${existing.userName || existing.email || "Guest"} cancelled their booking.`,
        target: { entity: "workshop", route: "/workshops" },
        severity: "warning",
        meta: { bookingId: id, workshopId: existing.workshopId },
      });
    }
  }, [persistBookings, addNotification]);

  useEffect(() => {
    const onRestore = () => {
      if (hasWorkspace) hydrate();
    };
    
    const onDisconnect = () => {
      // CRITICAL: Clear all data when workspace disconnected
      setWorkshops([]);
      setBookings([]);
    };
    
    // Only add these listeners if we have storage context
    if (storageContext) {
      window.addEventListener("mudo:vault-restored", onRestore);
      window.addEventListener("mudo:files-restored", onRestore);
      window.addEventListener("mudo:workspace-disconnected", onDisconnect);
      return () => {
        window.removeEventListener("mudo:vault-restored", onRestore);
        window.removeEventListener("mudo:files-restored", onRestore);
        window.removeEventListener("mudo:workspace-disconnected", onDisconnect);
      };
    }
  }, [hasWorkspace, hydrate, storageContext]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === lsKey(PATH_WORKSHOPS)) {
        try {
          const v = JSON.parse(e.newValue || "[]");
          if (!deepEqual(v, workshopsRef.current)) setWorkshops(safeArr(v));
        } catch {}
      }
      if (e.key === lsKey(PATH_BOOKINGS)) {
        try {
          const v = JSON.parse(e.newValue || "[]");
          if (!deepEqual(v, bookingsRef.current)) setBookings(safeArr(v));
        } catch {}
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    let t = null;
    const BASE_POLL_MS = 15000;
    const POLL_MS = BASE_POLL_MS + drivePollJitterMsRef.current;
    async function tick() {
      try {
        // Only poll if we have storage context and Drive is linked
        if (!storageContext || !hasWorkspace || !isDriveLinked || !loadJSONFresh) return;
        const [rw, rb] = await Promise.all([
          loadJSONFresh(PATH_WORKSHOPS, []),
          loadJSONFresh(PATH_BOOKINGS,  []),
        ]);
        if (!deepEqual(rw, workshopsRef.current)) {
          mirrorToLocal(PATH_WORKSHOPS, rw);
          setWorkshops(safeArr(rw));
          window.dispatchEvent(new Event("mudo:workshops-updated"));
        }
        if (!deepEqual(rb, bookingsRef.current)) {
          mirrorToLocal(PATH_BOOKINGS, rb);
          setBookings(safeArr(rb));
          window.dispatchEvent(new Event("mudo:workshops-updated"));
        }
      } finally {
        t = setTimeout(tick, POLL_MS);
      }
    }
    if (storageContext && isDriveLinked) t = setTimeout(tick, POLL_MS);
    return () => t && clearTimeout(t);
  }, [hasWorkspace, isDriveLinked, loadJSONFresh, mirrorToLocal, storageContext]);

  const getWorkshopById = useCallback((id) => workshopsRef.current.find(w => w.id === id) || null, []);
  const listBookingsFor = useCallback((workshopId) => bookingsRef.current.filter(b => b.workshopId === workshopId), []);
  const totalSeatsBooked = useCallback((workshopId) =>
    listBookingsFor(workshopId).reduce((sum, b) => sum + (Number(b.seats) || 0), 0),
    [listBookingsFor]
  );

  // Manual sync function to pull workshops from Supabase into vault
  const syncFromSupabase = useCallback(async () => {
    if (!user) {
      console.warn('[WorkshopContext] Cannot sync: user not authenticated');
      return { ok: false, error: 'Authentication required' };
    }
    
    // Get studio ID from studioProfile first, or query studios table
    let studioId = studioProfile?.id;
    
    // Fallback: query studios table to find studio for this user
    if (!studioId) {
      try {
        const { data: studios, error } = await supabase
          .from('studios')
          .select('id')
          .eq('owner_user_id', user.id)
          .limit(1);
        
        if (error) {
          console.warn('[WorkshopContext] Studios query error:', error);
        } else if (studios && studios.length > 0) {
          studioId = studios[0].id;
          console.log('[WorkshopContext] Found studio_id from studios table:', studioId);
        }
      } catch (err) {
        console.warn('[WorkshopContext] Failed to query studios table:', err.message);
      }
    }
    
    if (!studioId) {
      console.warn('[WorkshopContext] Cannot sync: no studio_id found', { 
        studioProfileId: studioProfile?.id,
        userId: user?.id
      });
      return { ok: false, error: 'Studio ID not found' };
    }
    
    console.log('[WorkshopContext] Starting sync from Supabase...', { userId: user?.id, studioId });

    try {
      const { data: supabaseWorkshops, error } = await getStudioWorkshops(
        studioId,
        { includeArchived: true, limit: 1000 }
      );

      if (error) throw error;

      if (!supabaseWorkshops || supabaseWorkshops.length === 0) {
        console.log('[WorkshopContext] No workshops found in Supabase');
        return { ok: true, synced: 0 };
      }

      // Transform Supabase → vault format
      const transformed = supabaseWorkshops.map(sw => ({
        id: sw.id,
        title: sw.title || "",
        description: sw.description || "",
        startISO: sw.start_at,
        startAt: sw.start_at,
        endAt: sw.end_at,
        date: sw.start_at,
        durationMins: sw.duration_mins || 120,
        duration: sw.duration_mins || 120,
        capacity: sw.capacity || 10,
        seatsTaken: sw.seats_taken || 0,
        price: (sw.price_cents || 0) / 100,
        currency: sw.currency || "aud",
        level: sw.skill_level || "All Levels",
        skillLevel: sw.skill_level || "All Levels",
        materialsPerPerson: sw.materials_list ? JSON.parse(sw.materials_list) : [],
        images: sw.images ? JSON.parse(sw.images) : null,
        isPublic: sw.is_published !== false,
        createdAt: sw.created_at,
        updatedAt: sw.updated_at,
        archivedAt: sw.archived_at || null,
        studio_id: studioId,
      }));

      // Merge with existing vault workshops
      const vaultById = new Map(workshopsRef.current.map(ws => [ws.id, ws]));
      transformed.forEach(sw => vaultById.set(sw.id, sw));
      const merged = Array.from(vaultById.values());

      await persistWorkshops(merged);
      console.log(`[WorkshopContext] Synced ${supabaseWorkshops.length} workshops from Supabase`);

      return { ok: true, synced: supabaseWorkshops.length };
    } catch (err) {
      console.error('[WorkshopContext] Sync from Supabase failed:', err);
      return { ok: false, error: err.message };
    }
  }, [user, studioProfile, writeDomain, persistWorkshops]);

  // Expose sync function to window for debugging/manual triggers from console
  useEffect(() => {
    window.__mudoSyncFromSupabase = syncFromSupabase;
    return () => { delete window.__mudoSyncFromSupabase; };
  }, [syncFromSupabase]);

  const value = useMemo(() => ({
    loading,
    workshops,
    bookings,

    upsertWorkshop,
    removeWorkshop,
    deleteWorkshop: removeWorkshop,    // back-compat alias

    createWorkshop: upsertWorkshop,         // back-compat alias
    createBooking: bookWorkshop,            // back-compat alias

    bookWorkshop,
    updateBooking,
    cancelBooking,

    getWorkshopById,
    listBookingsFor,
    totalSeatsBooked,

    syncFromSupabase,                       // Manual sync function
  }), [
    loading, workshops, bookings,
    upsertWorkshop, removeWorkshop,
    bookWorkshop, updateBooking, cancelBooking,
    getWorkshopById, listBookingsFor, totalSeatsBooked,
    syncFromSupabase,
  ]);

  return <WorkshopContext.Provider value={value}>{children}</WorkshopContext.Provider>;
}
